import { Stack, Table, Text, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import type { IconProps } from "@tabler/icons-react";
import type { ComponentType } from "react";
import { Link } from "react-router-dom";
import { DataTableShell, FilterBar, PageHeader, SortableTh, useInfinitePage, useTableSort } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { kycStatusLabel } from "../../lib/kyc";
import { NAV_ICONS } from "../../lib/navCopy";
import { useQueryFilters } from "../../lib/useQueryFilters";
import type { CompanyType, User } from "../../types/api";

const PEOPLE_FILTER_DEFAULTS = { q: "" };
const PEOPLE_FILTER_DEBOUNCE_KEYS = ["q"] as const;
const PEOPLE_FILTER_OPTIONS = { debounceKeys: PEOPLE_FILTER_DEBOUNCE_KEYS };

export function AdminPeoplePage({
  role,
  companyType,
  title = "People",
  subtitle = "Platform users by role.",
  icon = NAV_ICONS.candidates,
}: {
  role?: User["role"];
  companyType?: CompanyType;
  title?: string;
  subtitle?: string;
  icon?: ComponentType<IconProps>;
}) {
  const peopleFilters = useQueryFilters(PEOPLE_FILTER_DEFAULTS, PEOPLE_FILTER_OPTIONS);
  const search = peopleFilters.draft.q;
  const peopleQuery = useInfinitePage(["admin-people", role, companyType, peopleFilters.values.q], (page, pageSize) =>
    adminApi.listPeople({
      q: peopleFilters.values.q || undefined,
      role,
      company_type: companyType,
      page,
      page_size: pageSize,
    }),
  );

  const people = peopleQuery.items;
  const { sorted: sortedPeople, sortKey, direction, toggle } = useTableSort(people, {
    name: (r) => r.full_name,
    email: (r) => r.email,
    role: (r) => r.role,
    company: (r) => r.company_name ?? "",
    status: (r) => r.status,
    kyc: (r) => r.kyc_status ?? "",
    lastLogin: (r) => r.last_login_at,
  });
  const showCompany = role === "company_user";

  return (
    <Stack gap="lg">
      <PageHeader icon={icon} title={title} subtitle={subtitle} />

      <FilterBar>
        <TextInput
          placeholder="Search by name or email"
          leftSection={<IconSearch size="var(--axio-unit-16)" />}
          value={search}
          onChange={(e) => peopleFilters.set({ q: e.currentTarget.value })}
          w="var(--axio-unit-320)"
        />
      </FilterBar>

      <DataTableShell
        loading={peopleQuery.isLoading}
        error={
          peopleQuery.isError
            ? peopleQuery.error instanceof Error
              ? peopleQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void peopleQuery.refetch()}
        hasMore={peopleQuery.hasMore}
        loadingMore={peopleQuery.loadingMore}
        onLoadMore={peopleQuery.loadMore}
        isEmpty={people.length === 0}
        empty={{
          icon: icon,
          title: "No people found",
          description: search ? "Try a different search term." : "Users in this role will appear here.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="name" sortKey={sortKey} direction={direction} onSort={toggle}>
                Name
              </SortableTh>
              <SortableTh columnKey="email" sortKey={sortKey} direction={direction} onSort={toggle}>
                Email
              </SortableTh>
              <SortableTh columnKey="role" sortKey={sortKey} direction={direction} onSort={toggle}>
                Role
              </SortableTh>
              {showCompany && (
                <SortableTh columnKey="company" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Company
                </SortableTh>
              )}
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <SortableTh columnKey="kyc" sortKey={sortKey} direction={direction} onSort={toggle}>
                KYC
              </SortableTh>
              <SortableTh columnKey="lastLogin" sortKey={sortKey} direction={direction} onSort={toggle}>
                Last login
              </SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedPeople.map((row) => (
              <Table.Tr key={row.user_id}>
                <Table.Td>
                  <Text
                    component={Link}
                    to={
                      role === "affiliate" && row.affiliate_id
                        ? `/admin/affiliates/${row.affiliate_id}`
                        : `/admin/people/${row.user_id}`
                    }
                    fw="var(--axio-font-weight-semibold)"
                  >
                    {row.full_name}
                  </Text>
                </Table.Td>
                <Table.Td>{row.email}</Table.Td>
                <Table.Td>{row.role}</Table.Td>
                {showCompany && <Table.Td>{row.company_name ?? "—"}</Table.Td>}
                <Table.Td>{row.status}</Table.Td>
                <Table.Td>
                  {row.kyc_status ? kycStatusLabel(row.kyc_status) : "—"}
                  {row.kyc_bypass ? " · bypass" : ""}
                </Table.Td>
                <Table.Td>{row.last_login_at ? formatDate(row.last_login_at) : "—"}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>
    </Stack>
  );
}
